<template>

  <div>
    <div class="row">
      <div class="col-12">
        <div class="page-title-box">
          <div class="page-title-right">
            <form class="form-inlines">
              <a href="#" @click.prevent="prepareContract" class="btn btn-link ml-2">
                <i class="mdi mdi-file-pdf  mr-1"></i>Lepingu pdf...
              </a>
              <submit-button class="btn ml-2" variant="link" @click.prevent="createAdvancePaymentInvoice">
                <i class="mdi mdi-plus  mr-1"></i>Loo ettemaksu nõue
              </submit-button>
              <submit-button class="ml-2" variant="primary" @click.prevent="createInvoice">
                <i class="mdi mdi-plus mr-1"></i>Loo arve
              </submit-button>
            </form>
          </div>
          <h4 class="page-title" v-if="client">{{ client.name }}</h4>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-12 col-xl-6">

        <div class="card" v-if="client">
          <div class="card-body">
            <h4 class="card-title mt-0 mb-2">Üldandmed</h4>

            <div class="alert alert-warning alert-card-edge-to-edge" v-for="memo in client.memos">
              <permission name="ClientMemoDelete" class="float-right">
                <button type="button" class="btn btn-link" @click="removeMemo(memo)"><i class="mdi mdi-delete"></i>
                </button>
              </permission>
              {{ memo.message }}<br>
              <small class="text-opaque">{{ memo.createdBy }} {{ memo.createdAt | formatDateTime }} </small>
            </div>

            <div class="text-left">

              <div class="row">
                <div class="col-md-6">
                  <div class="data-list">
                    <dl>
                      <dt>Kliendikood</dt>
                      <dd>{{ client.shortCode }}</dd>
                    </dl>
                    <dl v-if="client.status && client.status.value">
                      <dt>Staatus</dt>
                      <dd>
                        <span
                          :class="'badge badge-lg badge-' + statusStyleClass(client.status.value)">{{
                            client.status.text
                          }}</span>
                        <a href="#" @click.prevent="editClientStatus" class="card-link text-primary"><i
                          class="mdi mdi-pencil"></i></a>
                      </dd>
                    </dl>
                    <dl v-if="client.regCode">
                      <dt>{{ regCodeLabel() }}</dt>
                      <dd>{{ client.regCode }}</dd>
                    </dl>
                    <dl v-if="client.vatin">
                      <dt>KMKR</dt>
                      <dd>{{ client.vatin }}</dd>
                    </dl>
                    <dl>
                      <dt>KM %</dt>
                      <dd v-if="client.vatRate" class="vat-rate">{{ client.vatRate }}</dd>
                      <dd v-if="!client.vatRate" class="vat-rate">määramata</dd>
                    </dl>
                    <dl v-if="client.phone || client.email">
                      <dt>Kontakt</dt>
                      <dd>
                        <div v-if="client.phone">
                          <a :href="`tel:${client.phone}`">{{ client.phone }}</a>
                        </div>
                        <div v-if="client.email">
                          <a :href="`mailto:${client.email}`">{{ client.email }}</a>
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="data-list">
                    <dl v-if="client.manager">
                      <dt>Kliendihaldur</dt>
                      <dd>{{ client.manager.fullName }}</dd>
                    </dl>
                    <dl v-if="client.language">
                      <dt>Suhtluskeel</dt>
                      <dd>{{ languages[client.language] }}</dd>
                    </dl>
                    <dl v-if="client.fieldOfActivity">
                      <dt>Tegevusala</dt>
                      <dd>{{ fieldOfActivityName(client.fieldOfActivity) }}</dd>
                    </dl>
                    <dl v-if="client.address">
                      <dt>Aadress</dt>
                      <dd class="small">
                        <div v-if="client.address.street">{{ client.address.street }}</div>
                        <div v-if="client.address.city">{{ client.address.city }}</div>
                        <div>{{ client.address.locality }} {{ client.address.zip }}</div>
                        <div v-if="client.address.country">{{ client.address.country.text }}</div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-2">
              <a href="#" @click.prevent="editClient" class="card-link text-primary edit-client">
                <i class="mdi mdi-pencil mr-1"></i>Üldandmed
              </a>
              <permission name="ClientStatusUpdate">
                <a href="#" @click.prevent="editClientStatus" class="card-link text-primary ml-2">
                  <i class="mdi mdi-pencil mr-1"></i>Staatus
                </a>
              </permission>
              <permission name="ClientMemoCreate">
                <a href="#" @click.prevent="addMemo" class="card-link text-primary ml-2">
                  <i class="mdi mdi-comment-text mr-1"></i>Memo
                </a>
              </permission>
              <p v-if="client.updatedBy" class="card-text float-right">
                <small class="text-muted">Uuendas: {{ client.updatedBy }}</small>
              </p>

            </div>

          </div>
        </div>

        <div class="card" v-if="client">
          <div class="card-body">
            <h4 class="card-title mt-0 mb-2">Maksetingimused</h4>
            <div class="row" v-if="client.paymentConditions">
              <div class="col">
                <div class="data-list">
                  <dl>
                    <dt>Tähtaeg</dt>
                    {{ client.paymentConditions.paymentDueDays }} <small>päeva</small>
                  </dl>
                </div>
              </div>
              <div class="col">
                <div class="data-list">
                  <dl>
                    <dt>Min. hind</dt>
                    {{ client.paymentConditions.minimumFee }} <small>&euro;</small>
                  </dl>
                </div>
              </div>
              <div class="col" v-if="client.paymentConditions.dailyFeeRate">
                <div class="data-list">
                  <dl>
                    <dt>Päeva tasu %</dt>
                    {{ client.paymentConditions.dailyFeeRate }}
                  </dl>
                </div>
              </div>
              <div class="col" v-if="client.paymentConditions.periodFeeRate">
                <div class="data-list">
                  <dl>
                    <dt>Kogu tasu %</dt>
                    {{ client.paymentConditions.periodFeeRate }}
                  </dl>
                </div>
              </div>
              <div class="col" v-if="client.paymentConditions.discount">
                <div class="data-list">
                  <dl>
                    <dt>Allahindluse %</dt>
                    {{ client.paymentConditions.discount }}
                  </dl>
                </div>
              </div>
            </div>

            <div class="row mt-1">
              <div class="col">
                <permission name="PaymentConditionsUpdate">
                  <a @click="editPaymentConditions" class="card-link text-primary">
                    <i class="mdi mdi-pencil"></i>
                    <template v-if="client.paymentConditions">Muuda</template>
                    <template v-else>Määra</template>
                  </a>
                </permission>
              </div>
            </div>
          </div>
        </div>

        <div class="card" v-if="client">
          <div class="card-body">
            <h4 class="card-title mt-0">Dokumendid</h4>
            <table v-if="documents.length" class="table table-centered table-sm mt-3 documents">
              <tbody>
              <tr v-for="document in documents">
                <td class="pl-0">
                  <a v-if="document.present" target="_blank"
                     :href="`/api/clients/${clientId}/documents/${document.id}`">{{ document.name }}</a>
                  <span v-else>{{ document.name }}</span>
                </td>
                <td>{{ document.category }}</td>
                <td class="table-action text-nowrap pr-0">
                  <permission name="DocumentDelete">
                    <a href="#" @click.prevent="deleteDocument(document)" class="action-icon">
                      <i class="mdi mdi-delete"></i>
                    </a>
                  </permission>
                </td>
              </tr>
              </tbody>
            </table>
            <a href="#" @click.prevent="addDocument" class="card-link text-primary">
              <i class="mdi mdi-plus"></i> Lisa dokument
            </a>
          </div>
        </div>
      </div>

      <div class="col-md-12 col-xl-6"> <!--right column-->
        <div class="card" v-if="client">
          <div class="card-body">
            <h4 class="card-title mt-0 mb-2">Krediidilimiit</h4>

            <div class="alert alert-danger alert-card-edge-to-edge" v-if="creditLimitStatus.overdueAmount">
              <router-link to="/invoices" class="float-right">Vaata arveid <i class="mdi mdi-chevron-right"></i>
              </router-link>
              Võlgnevus <strong>{{ creditLimitStatus.overdueAmount | amountWithCurrency }}</strong>
            </div>
            <div class="alert alert-info alert-card-edge-to-edge" v-if="prepayment">
              Ettemaks <strong>{{ prepayment | amountWithCurrency }}</strong>
            </div>

            <div class="progress progress-md progress-card bg-success" v-if="creditLimitStatus.limit">
              <div class="progress-bar"
                   :class="{'bg-warning-lighten': usedLimitPercentage <= 99, 'bg-error': usedLimitPercentage > 99}"
                   :style="{width: usedLimitPercentage + '%'}"></div>
            </div>

            <div class="row">
              <div class="col">
                <div class="data-list">
                  <dl>
                    <dt>Määratud limiit</dt>
                    <dd v-if="client.creditLimitTotal">{{ client.creditLimitTotal | amountWithCurrency }}</dd>
                    <dd v-else>puudub</dd>
                  </dl>
                </div>
              </div>
              <div class="col" v-if="creditLimitStatus.usedLimit">
                <div class="data-list">
                  <dl>
                    <dt>Kasutatud limiit</dt>
                    <dd>{{ this.creditLimitStatus.usedLimit | amountWithCurrency }}</dd>
                  </dl>
                </div>
              </div>
              <div class="col" v-if="creditLimitStatus.limit">
                <div class="data-list">
                  <dl>
                    <dt>Vaba limiit</dt>
                    <dd>{{ unusedCreditLimit | amountWithCurrency }}</dd>
                  </dl>
                </div>
              </div>
              <div class="col">
                <div class="data-list">
                  <dl>
                    <dt>
                      Distsipliin
                      <i class="mdi mdi-help-circle" v-b-tooltip.hover
                         title="Arvete keskmine laekumine tähtaja suhtes"></i>
                    </dt>
                    <dd>
                      <span v-if="client.invoiceAveragePaidWithinDays !== undefined">
                        {{ client.invoiceAveragePaidWithinDays }} päeva
                      </span>
                      <span v-else>viimase 12 kuu andmed puuduvad</span>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>

            <div class="row mt-2">
              <div class="col">
                <permission name="CreditLimitUpdate">
                  <a @click="editCreditLimit" class="card-link text-primary">
                    <i class="mdi mdi-pencil"></i>
                    <template v-if="client.creditLimitTotal">Muuda limiiti</template>
                    <template v-else>Määra limiit</template>
                  </a>
                </permission>
              </div>
              <div class="col text-right">
                <small v-if="client.creditLimitTotal" class="text-muted">
                  Määramise kuupäev
                  <span v-if="client.creditLimitDate">{{ client.creditLimitDate }}</span>
                  <span v-else>teadmata</span>
                </small>
              </div>
            </div>
          </div>
        </div>

        <div class="card" v-if="client">
          <div class="card-body">
            <h4 class="card-title mt-0 mb-2">Arved</h4>
            <div class="row mb-2">
              <div class="col">
                <div class="data-list">
                  <dl v-if="client.invoiceEmails">
                    <dt>Arvete e-mail</dt>
                    <dd v-if="client.invoiceEmails.length">
                      <div v-for="email in client.invoiceEmails">
                        <a :href="`mailto:${email}`">{{ email }}</a></div>
                    </dd>
                    <dd v-if="!client.invoiceEmails.length">puudub</dd>
                  </dl>
                </div>
              </div>
              <div class="col">
                <div class="data-list">
                  <dl>
                    <dt>Eristused arvele</dt>
                    <dd v-if="client.detailedInvoice">lisatakse</dd>
                    <dd v-if="!client.detailedInvoice">ei lisata</dd>
                  </dl>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col">
                <a href="#" @click.prevent="$refs.editInvoiceSettingsModal.show(client)" class="card-link text-primary">
                  <i class="mdi mdi-pencil"></i>&nbsp;Muuda
                </a>
              </div>
              <div class="col text-right">
                <router-link to="/invoices" class="card-link text-primary">
                  Vaata arveid<i class="mdi mdi-chevron-right ml-1"></i>
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <div class="card" v-if="client">
          <div class="card-body">
            <h4 class="card-title mt-0">Esindajad</h4>
            <table v-if="client.representatives.length" class="table table-centered table-sm mb-2">
              <tbody>
              <tr v-for="representative in client.representatives">
                <td class="pl-0">
                  <strong>{{ representative.firstName }}&nbsp;{{ representative.lastName }}</strong>
                  <div v-if="representative.type" class="small text-muted">
                    {{ representativeTypes[representative.type] }}
                  </div>
                  <div v-if="representative.details"><i>{{ representative.details }}</i></div>
                </td>
                <td>
                  <div v-if="representative.phone" class="text-nowrap"><a
                    :href="`tel:${representative.phone}`">{{ representative.phone }}</a></div>
                  <div v-if="representative.email" class="text-nowrap"><a
                    :href="`mailto:${representative.email}`">{{ representative.email }}</a></div>
                </td>
                <td class="table-action text-right text-nowrap pr-0">
                  <a class="action-icon" @click="editRepresentative(representative)">
                    <i class="mdi mdi-pencil"></i>
                  </a>
                  <permission name="RepresentativeDelete">
                    <a class="action-icon" @click="deleteRepresentative(representative)">
                      <i class="mdi mdi-delete"></i>
                    </a>
                  </permission>
                </td>
              </tr>
              </tbody>
            </table>
            <a href="#" @click.prevent="addRepresentative" class="card-link text-primary">
              <i class="mdi mdi-plus mr-1"></i>Lisa esindaja
            </a>
          </div>
        </div>

        <client-pricelist v-if="clientId" :client-id="clientId"/>
      </div>
    </div>

    <modal ref="editInvoiceSettingsModal" title="Muuda arve parameetreid" size="md" @ok="load">
      <edit-invoice-settings :client="data" slot-scope="{close, visible, data}" @done="close" v-if="visible"/>
    </modal>

    <modal ref="prepareContractModal" size="lg" title="Teeninduslepingu ettevalmistamine">
      <prepare-contract :client-id="clientId" slot-scope="{close, visible}" @done="close" v-if="visible"/>
    </modal>

    <modal ref="editClientStatusModal" size="md" title="Kliendi staatuse muutmine" @ok="load">
      <edit-client-status :client="data" slot-scope="{close, visible, data}" @done="close" v-if="visible"/>
    </modal>

    <modal ref="editClientModal" @ok="load">
      <edit-client :client-id="clientId" slot-scope="{setTitle, close, visible}" @title="setTitle" @done="close"
                   v-if="visible"/>
    </modal>

    <modal ref="editRepresentativeModal" @ok="load">
      <edit-representative :client-id="clientId" :id="data.id" slot-scope="{setTitle, close, visible, data}"
                           @title="setTitle" @done="close" v-if="visible"/>
    </modal>

    <modal ref="editCreditLimitModal" title="Muuda krediitilimiiti" size="md" @ok="load">
      <edit-credit-limit :client="data" slot-scope="{close, visible, data}" @done="close" v-if="visible"/>
    </modal>

    <modal ref="editPaymentConditionsModal" title="Muuda maksetingimusi" size="md" @ok="load">
      <edit-payment-conditions :client="data" slot-scope="{close, visible, data}" @done="close" v-if="visible"/>
    </modal>

    <modal ref="addRepresentativeModal" title="Lisa esindaja" size="lg" @ok="load">
      <add-representative :client-id="clientId" slot-scope="{close, visible}" @done="close" v-if="visible"/>
    </modal>

    <modal ref="uploadDocumentModal" title="Lisa dokument" size="lg" @ok="load">
      <upload-document :client-id="clientId" slot-scope="{close, visible}" @done="close" v-if="visible"/>
    </modal>

    <modal ref="addMemoModal" title="Lisa memo" size="md" @ok="load">
      <add-memo :client="client" slot-scope="{close, visible}" @done="close" v-if="visible"/>
    </modal>
  </div>
</template>

<script>
import gateway from '@/services/gateway'
import Modal from '@/components/Modal'
import EditClient from '@/components/client/EditClient'
import EditClientStatus from '@/components/client/EditClientStatus'
import EditRepresentative from '@/components/client/EditRepresentative'
import EditCreditLimit from '@/components/client/EditCreditLimit'
import EditPaymentConditions from '@/components/client/EditPaymentConditions'
import EditInvoiceSettings from '@/components/client/EditInvoiceSettings'
import PrepareContract from '@/components/client/PrepareContract'
import AddRepresentative from '@/components/client/AddRepresentative'
import UploadDocument from '@/components/client/UploadDocument'
import AddMemo from '@/components/client/AddMemo'
import ClientPricelist from '@/components/client/ClientPricelist'
import {representativeTypes} from '@/services/Configuration'
import ClientIdMixin from '../mixins/ClientIdMixin'
import ClientStatusStyleClass from '../mixins/ClientStatusStyleClass'

export default {
  mixins: [ClientIdMixin, ClientStatusStyleClass],
  components: {
    Modal,
    EditRepresentative,
    EditClient,
    PrepareContract,
    AddRepresentative,
    UploadDocument,
    EditCreditLimit,
    EditPaymentConditions,
    EditInvoiceSettings,
    EditClientStatus,
    ClientPricelist,
    AddMemo
  },
  data() {
    return {
      client: null,
      prepayment: null,
      creditLimitStatus: {usedLimit: 0, limit: 0},
      documents: [],
      fieldsOfActivity: {},
      representativeTypes: representativeTypes,
      languages: {et: 'Eesti', en: 'Inglise', ru: 'Vene'}
    }
  },
  beforeCreate() {
    gateway.get('/classifiers?name=client.activity').then(r => {
      this.fieldsOfActivity = r.data
    })
  },
  methods: {
    addMemo() {
      this.$refs.addMemoModal.show()
    },
    removeMemo(memo) {
      this.$confirm('Kustutame memo?', () => {
        gateway.delete(`/clients/${this.clientId}/memos/${memo.id}`).then(() => {
          this.$notifications.info('Memo on kustutatud')
          this.load()
        })
      })
    },
    addDocument() {
      this.$refs.uploadDocumentModal.show()
    },
    deleteDocument(document) {
      this.$confirm('Kustutame dokumendi ' + document.name + '?', () => {
        gateway.delete(`/clients/${this.clientId}/documents/${document.id}`).then(() => {
          this.$notifications.info('Dokument kustutatud')
          this.load()
        })
      })
    },
    load() {
      gateway.get(`/clients/${this.clientId}`).then(r => {
        this.client = r.data
      })
      gateway.get(`/clients/${this.clientId}/documents`).then(r => {
        this.documents = r.data
      })
      gateway.get(`/clients/${this.clientId}/credit-limit-status`).then(r => {
        this.creditLimitStatus = r.data
      })
      gateway.get(`/clients/${this.clientId}/prepayment`)
        .then(r => {
          this.prepayment = r.data
        })
        .catch(r => {
          this.$notifications.warning(r.response ? r.response.data.key : 'Tehniline viga')
        })
    },
    addRepresentative() {
      this.$refs.addRepresentativeModal.show()
    },
    prepareContract() {
      this.$refs.prepareContractModal.show()
    },
    editClient() {
      this.$refs.editClientModal.show()
    },
    editClientStatus() {
      this.$refs.editClientStatusModal.show(this.client)
    },
    editRepresentative(representative) {
      this.$refs.editRepresentativeModal.show(representative)
    },
    editCreditLimit() {
      this.$refs.editCreditLimitModal.show(this.client)
    },
    editPaymentConditions() {
      this.$refs.editPaymentConditionsModal.show(this.client)
    },
    deleteRepresentative(representative) {
      this.$confirm(`Kustuta esindaja ${representative.firstName} ${representative.lastName}?`, () => {
        gateway
          .delete(`/clients/${this.clientId}/representatives/${representative.id}`)
          .then(() => this.load())
          .then(() => this.$notifications.info('Kliendi esindaja on kustutatud'))
      })
    },
    regCodeLabel() {
      return this.client.type === 'company' ? 'Registrikood:' : 'Isikukood:'
    },
    fieldOfActivityName(key) {
      return this.fieldsOfActivity[key]
    },
    createInvoice() {
      gateway.post(`/clients/${this.client.id}/invoice`).then(r => {
        let id = r.data
        this.$notifications.info(`Arve ${id} on loodud`)
        this.$router.push(`/invoices/${id}`)
      })
    },
    createAdvancePaymentInvoice() {
      gateway.post(`/clients/${this.client.id}/advance-payment-invoice`).then(r => {
        let id = r.data
        this.$notifications.info(`Ettemaksunõue ${id} on loodud`)
        this.$router.push(`/invoices/${id}`)
      })
    }
  },
  computed: {
    unusedCreditLimit() {
      return (this.creditLimitStatus.limit || 0) - this.creditLimitStatus.usedLimit
    },
    usedLimitPercentage() {
      if (!this.creditLimitStatus.limit) return 0
      return 100 * this.creditLimitStatus.usedLimit / this.creditLimitStatus.limit
    }
  }
}
</script>
